import { Tier } from './tier.model';
import { Currency } from './Currency';
import { WidgetDeviceView } from 'app/campaign/widgets.model';

export interface AccountDimensions {
  settings?: string[];
  custom?: string;
  scoreRetentionDays?: number;
  /** Category trail level the script reports; null means the default level. */
  categoryLevel?: number | null;
}

export interface HoldoutGroupSettings {
  percentage: number;
}

export interface EventRetentionConfig {
  pageviewTtlDays?: number;
  standardTtlDays?: number;
  attributionTtlDays?: number;
  lifetimeEventsTtlDays?: number;
  lifetimeEventsGoalNames?: string[];
}

// Values are the backend enum member names (the Gateway serializes enums by name), not camelCase.
export enum ElementPresence {
  LoggedIn = 'LoggedIn',
  LoggedOut = 'LoggedOut',
}

export interface DataLayerRule {
  key?: string;
  valueEquals?: string;
  valueEqualsLoggedOut?: string;
}

export interface JavascriptRule {
  expression?: string;
  loggedOutExpression?: string;
}

export interface ElementRule {
  selector?: string;
  presenceMeans?: ElementPresence;
  loggedOutSelector?: string;
}

// At most one rule may be set (enforced server-side).
export interface LoggedInDetectionConfig {
  dataLayerRule?: DataLayerRule;
  javascriptRule?: JavascriptRule;
  elementRule?: ElementRule;
}

// Values are the backend enum member names (the Gateway serializes enums by name), not camelCase.
export enum TrackingRuleMode {
  Always = 'Always',
  Never = 'Never',
  Cookie = 'Cookie',
  JavascriptExpression = 'JavascriptExpression',
}

export enum CookieMatchOperator {
  Equals = 'Equals',
  Contains = 'Contains',
  Exists = 'Exists',
}

export interface CookieRule {
  name?: string;
  operator?: CookieMatchOperator;
  value?: string;
}

export interface TrackingCapabilityRule {
  mode?: TrackingRuleMode;
  cookies?: CookieRule[];
  /** Only sent in JavascriptExpression mode; truthy on the page grants the capability. */
  expression?: string;
}

/** What is kept of a widget form submission while the traffic rule denies tracking. */
export enum FormSubmissionHandling {
  Store = 'Store',
  ForwardOnly = 'ForwardOnly',
}

export interface VisitorTrackingConfig {
  traffic?: TrackingCapabilityRule;
  rememberAcrossVisits?: TrackingCapabilityRule;
  formSubmissions?: FormSubmissionHandling;
  /** Counting a visitor who has not answered the traffic rule. Absent means not counted, unlike the others. */
  stateless?: TrackingCapabilityRule;
}

export interface WidgetsTrackingConfig {
  show?: TrackingCapabilityRule;
  /** Whether a closed experience stays closed beyond this visit. Absent means it does. */
  rememberAcrossVisits?: TrackingCapabilityRule;
}

/** What the onsite scripts do the moment a visitor answers. */
export interface PrivacyChangeConfig {
  /** Absent means on. */
  reinitializeWidgets?: boolean;
  /** Absent means on. */
  closeWidgets?: boolean;
}

export interface TrackingConfig {
  visitors?: VisitorTrackingConfig;
  widgets?: WidgetsTrackingConfig;
  onPrivacyChange?: PrivacyChangeConfig;
}

/** Whether some visitors are tracked without cookies: those the traffic rule does not track with them, and only if the account counts them. */
export function tracksWithoutCookies(tracking?: TrackingConfig): boolean {
  const traffic = tracking?.visitors?.traffic?.mode;
  const stateless = tracking?.visitors?.stateless?.mode;
  const trafficCanDeny = !!traffic && traffic !== TrackingRuleMode.Always;
  return trafficCanDeny && !!stateless && stateless !== TrackingRuleMode.Never;
}

export interface Account {
  nextId?: number;
  previousId?: number;
  maxBatchRecordsPerMonth?: number;
  maxContacts?: number;
  id?: string;
  siteId?: number;
  name?: string;
  isPoweredByDisabled?: boolean;
  createdTime?: string;
  updatedTime?: string;
  defaultFontFamily?: string;
  currency?: Currency;
  visitsTimeToLive?: number;
  lastSynced?: string;
  expireDate?: string;
  clientScript?: string;
  groupId?: string;
  tagForms?: boolean;
  useCookiesForTracking?: boolean;
  useSpaTracking?: boolean;
  useEcommerceAutocapture?: boolean;
  isAttributionActivated?: boolean;
  isExpired?: boolean;
  previewUrl?: string;
  isPaused?: boolean;
  trackingCode?: string;
  urls?: Array<string>;
  allowedApplicationIds?: Array<string>;
  androidStoreId?: string;
  iosStoreId?: string;
  metricsGoalNames?: Array<string>;
  type?: AccountType;
  mrr?: number;
  ipDb?: string;
  invoicingSystemAccountId?: number;
  identifier?: string;
  hasVisits?: boolean;
  deleteAction?: null | string;
  subscriberWorth?: number;
  isSyncDisabled?: boolean;
  useDataPlatform?: boolean;
  useServersideCookie?: boolean;
  tier?: Tier;
  daysToKeepUnmodifiedVisitors?: number;
  anonymizeForms?: boolean;
  language?: string;
  allowPersonalDataInBrowser?: boolean;
  isPublicApiActive?: boolean;
  defaultEditorView?: WidgetDeviceView;
  dimensions?: AccountDimensions;
  source?: SourceCoreModel;
  createdBy?: string;
  holdoutGroupSettings?: HoldoutGroupSettings;
  eventRetention?: EventRetentionConfig;
  loggedInDetection?: LoggedInDetectionConfig;
  tracking?: TrackingConfig;
  uiPreferences?: string;
  /** Absent or null means the account has its customer's CSM. */
  accountManagerOverride?: AccountManagerOverride | null;
}

/** An account's own CSM. A null userId means the account has No CSM even when its customer has one. */
export interface AccountManagerOverride {
  userId: string | null;
}

/**
 * @deprecated Use from customer instead of account
 */
export enum AccountType {
  Invoice = 'Invoice',
  SelfService = 'SelfService',
}

export enum SourceCoreModel {
  Backoffice = 'Backoffice',
  SelfService = 'SelfService',
  Upgrade = 'Upgrade',
  InvitedBy = 'InvitedBy',
}

export enum UseCases {
  LeadCapture = 'LeadCapture',
  LeadMagnet = 'LeadMagnet',
  MembershipAcquisition = 'MembershipAcquisition',
  ReferralCampaign = 'ReferralCampaign',
  VIPPreLaunchSignup = 'VIPPreLaunchSignup',
  SalesPromotion = 'SalesPromotion',
  DiscountOffer = 'DiscountOffer',
  DiscountReminder = 'DiscountReminder',
  ExitIntentOffer = 'ExitIntentOffer',
  ProductRecommendation = 'ProductRecommendation',
  UpsellCrossSellNudges = 'UpsellCrossSellNudges',
  CampaignAmplification = 'CampaignAmplification',
  PurchaseIntent = 'PurchaseIntent',
  LoginNudge = 'LoginNudge',
  PersonalizedOnsiteContent = 'PersonalizedOnsiteContent',
  GamificationCampaign = 'GamificationCampaign',
  ContestRaffle = 'ContestRaffle',
  ProfileEnrichment = 'ProfileEnrichment',
  ZeroPartyDataCollection = 'ZeroPartyDataCollection',
  CustomerFeedbackSurvey = 'CustomerFeedbackSurvey',
  ProductDiscoveryQuiz = 'ProductDiscoveryQuiz',
  WeatherForecastPromotions = 'WeatherForecastPromotions',
  ListBuilding = 'ListBuilding',
  LeadGenerationB2B = 'LeadGenerationB2B',
  InfluencerCollab = 'InfluencerCollab',
}

export enum DomainDataConfig {
  TopDomain = 'topDomain',
  SubDomain = 'subDomain',
  MarketAccount = '(market account',
}
